// PURE business logic — no framework imports, no Prisma, no NestJS.
//
// The single home for "is this address private / loopback / link-local / ULA /
// otherwise internal infrastructure".
//
// ## Three callers, one range table, and why they share it
//
// 1. The SSRF guard blocks these as outbound destinations. Two shapes read the
//    table: `safe-external-url.schema.ts` beside this file, which is the one
//    exported schema both `CustomModel` write boundaries import (the internal
//    one and Public API v1 — they disagreed until NEX-3898), and the webhook
//    guard `is-safe-webhook-url.ts` (public v1 schemas), which allows plain HTTP
//    by design. The backend's own delivery-time guard is no longer a string check
//    at all: `_common/network/resolve-safe-target.ts` reads this same table, then
//    RESOLVES and pins, which a package boundary cannot.
// 2. The rate limiter's client-IP resolver (`_common/throttler/resolve-client-ip.ts`)
//    skips these when walking `X-Forwarded-For` from the right, because they are
//    our own hops.
// 3. Express's `trust proxy` predicate (`_common/network/trust-proxy.ts`), which is
//    literally caller 2's function.
//
// Callers 1 and 2 want the range set for OPPOSITE reasons, so it is worth stating
// why one table is still correct — the answer is that they are not equally
// sensitive to it:
//
// - For the SSRF guard the table IS the whole control. A missing range is an open
//   hole, full stop.
// - For the resolver, correctness comes from the APPEND-ORDER INVARIANT, not from
//   the table: a hop we control appends the true peer to the RIGHT of anything a
//   caller can inject, and the walk stops at the first non-internal entry it meets
//   coming from the right. So a caller-injected entry is never reached, whatever
//   this table says about it. The table only decides how far right the walk stops,
//   and it is wrong in a costly direction only when it FAILS TO recognise one of
//   our own hops — then the resolver hands back an ingress address and every caller
//   shares one rate-limit bucket.
//
// So widening the table is safe for caller 2 and required by caller 1, and the two
// do not need to diverge. ⚠️ If the append-order invariant is ever broken — someone
// reads the leftmost entry, or a hop stops appending — that calculus inverts and
// this comment is wrong. Do not widen the table without re-reading it.
//
// ## The discriminator for what belongs here
//
// "Is this plausibly REACHABLE INTERNAL INFRASTRUCTURE", never "is this special-use
// per RFC 5735". Those are different questions and the second one is a trap: it
// would pull in the TEST-NET documentation ranges (192.0.2.0/24, 198.51.100.0/24,
// 203.0.113.0/24), which are not internal, are not an SSRF target, and are used
// throughout this repository's own specs as the canonical PUBLIC addresses. Adding
// them would break the resolver for no security gain.

/**
 * The canonical IPv4 range check, over already-split octets.
 *
 * `b` and `c` may be `NaN` when the caller had a partial address; every
 * comparison against `NaN` is false, so a partial address matches only the
 * ranges that are decided by the octets it actually supplied. That leniency is
 * deliberate and load-bearing — see `isPrivateIpv4`.
 */
function isPrivateIpv4Octets(a: number, b: number, c: number): boolean {
  if (a === 0) return true; // 0.0.0.0/8 "this network" (RFC 1122)
  if (a === 10) return true; // 10.0.0.0/8 private (RFC 1918)
  if (a === 127) return true; // 127.0.0.0/8 loopback (RFC 1122)
  if (a === 100 && b >= 64 && b <= 127) return true; // 100.64.0.0/10 CGNAT / shared address space (RFC 6598)
  if (a === 169 && b === 254) return true; // 169.254.0.0/16 link-local + cloud metadata (RFC 3927)
  if (a === 172 && b >= 16 && b <= 31) return true; // 172.16.0.0/12 private (RFC 1918)
  if (a === 192 && b === 0 && c === 0) return true; // 192.0.0.0/24 IETF protocol assignments (RFC 6890)
  if (a === 192 && b === 168) return true; // 192.168.0.0/16 private (RFC 1918)
  if (a === 198 && (b === 18 || b === 19)) return true; // 198.18.0.0/15 benchmarking, routed internally in practice (RFC 2544)
  if (a >= 224 && a <= 239) return true; // 224.0.0.0/4 multicast — never a unicast target (RFC 5771)
  if (a >= 240) return true; // 240.0.0.0/4 reserved, incl. 255.255.255.255 broadcast (RFC 1112)
  return false;
}

/**
 * Private / loopback / link-local / CGNAT / otherwise-internal IPv4.
 *
 * Expects a dotted quad. A malformed input yields `NaN` octets and falls
 * through to `false` (treated as public), so callers that must fail closed
 * normalize first — the SSRF guard does this by running the host through the
 * WHATWG URL parser, which canonicalises decimal, hex and octal spellings to a
 * dotted quad before this runs.
 *
 * A PARTIAL address is deliberately still classified on the octets it does
 * supply: `"10"` is internal. `resolve-client-ip` relies on that, because a
 * malformed `X-Forwarded-For` entry that we treat as public becomes the value a
 * rate limit is keyed on.
 */
export function isPrivateIpv4(ip: string): boolean {
  const [a = NaN, b = NaN, c = NaN] = ip.split(".").map(Number);
  return isPrivateIpv4Octets(a, b, c);
}

/**
 * The same range table over a 32-bit unsigned IPv4, for callers that already
 * parsed and validated the address numerically.
 */
export function isPrivateIpv4Uint32(ip: number): boolean {
  return isPrivateIpv4Octets((ip >>> 24) & 0xff, (ip >>> 16) & 0xff, (ip >>> 8) & 0xff);
}

/**
 * Expand an IPv6 address to its 8 hextets, or `null` if it is not one.
 *
 * 🚨 **Reading hextets by splitting on `":"` is wrong and it is a BYPASS, not a
 * cosmetic bug.** `::` stands for a variable-length run of zero groups, so the
 * position of a group is not its index in the split. The 6to4 unwrap did split
 * that way and required exactly two groups after `2002:`, which every 6to4
 * address whose embedded IPv4 ends in `.0.0` fails — the WHATWG URL parser
 * canonicalises `2002:6440:0:0:0:0:0:0` to `2002:6440::`. `2002:6440::` (=
 * 100.64.0.0), `2002:a00::` (= 10.0.0.0) and `2002:7f00::` (= 127.0.0.0) all
 * read as PUBLIC. Expanding first removes the whole class rather than the three
 * spellings someone thought to test.
 *
 * Also normalises a dotted-quad tail (`::ffff:127.0.0.1`) into two hextets, so
 * the compressed and uncompressed spellings of a mapped address agree.
 */
function hextets(address: string): readonly number[] | null {
  const bare = address.split("%")[0] ?? ""; // drop any zone id
  if (bare.length === 0 || !bare.includes(":")) return null;

  // A trailing `a.b.c.d` is two hextets. Rewrite it before counting groups.
  let head = bare;
  const lastColon = bare.lastIndexOf(":");
  const tail = bare.slice(lastColon + 1);
  if (tail.includes(".")) {
    const octets = tail.split(".").map(Number);
    if (octets.length !== 4) return null;
    if (octets.some((n) => !Number.isInteger(n) || n < 0 || n > 255)) return null;
    const [a = 0, b = 0, c = 0, d = 0] = octets;
    head = `${bare.slice(0, lastColon + 1)}${((a << 8) | b).toString(16)}:${((c << 8) | d).toString(16)}`;
  }

  const readGroups = (segment: string): number[] | null => {
    if (segment.length === 0) return [];
    const out: number[] = [];
    for (const group of segment.split(":")) {
      if (!/^[0-9a-f]{1,4}$/.test(group)) return null;
      out.push(parseInt(group, 16));
    }
    return out;
  };

  const sides = head.split("::");
  if (sides.length > 2) return null;
  const left = readGroups(sides[0] ?? "");
  if (left === null) return null;

  if (sides.length === 1) return left.length === 8 ? left : null;

  const right = readGroups(sides[1] ?? "");
  if (right === null) return null;
  const zeros = 8 - left.length - right.length;
  if (zeros < 1) return null; // `::` must stand for at least one group
  return [...left, ...Array<number>(zeros).fill(0), ...right];
}

const embeddedIpv4 = (hi: number, lo: number): string =>
  `${(hi >> 8) & 0xff}.${hi & 0xff}.${(lo >> 8) & 0xff}.${lo & 0xff}`;

/**
 * Loopback / unspecified / ULA / link-local / multicast / IETF-reserved IPv6,
 * plus any address carrying an embedded IPv4, which defers to the IPv4 table.
 */
export function isPrivateIpv6(ip: string): boolean {
  const groups = hextets(ip.trim().toLowerCase());
  if (groups === null) return false;

  const [g0 = 0, g1 = 0, g2 = 0, g3 = 0, g4 = 0, g5 = 0, g6 = 0, g7 = 0] = groups;

  // ::ffff:0:0/96 IPv4-mapped and ::/96 IPv4-compatible — resolve the embedded
  // address so it gets the IPv4 table's verdict. This also covers `::` (0.0.0.0)
  // and `::1` (loopback), both of which the IPv4 table already classifies.
  if (g0 === 0 && g1 === 0 && g2 === 0 && g3 === 0 && g4 === 0 && (g5 === 0xffff || g5 === 0)) {
    return isPrivateIpv4(embeddedIpv4(g6, g7));
  }

  // 2002::/16 6to4 (RFC 3056) tunnels to the IPv4 address in the next two hextets.
  if (g0 === 0x2002) return isPrivateIpv4(embeddedIpv4(g1, g2));

  // ::/8 — IETF-reserved and not globally routable. Covers the NAT64 well-known
  // prefix 64:ff9b::/96 (RFC 6052). ⚠️ A SHORT group is left-padded, so `fc::1`
  // is 0x00fc and lands here, NOT in the ULA range below.
  if (g0 <= 0x00ff) return true;
  if (g0 >= 0xfc00 && g0 <= 0xfdff) return true; // fc00::/7 unique-local (RFC 4193)
  if (g0 >= 0xfe80 && g0 <= 0xfebf) return true; // fe80::/10 link-local (RFC 4291)
  if (g0 >= 0xff00) return true; // ff00::/8 multicast — never a unicast target (RFC 4291)
  return false;
}
